import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useIdleLogout } from '../hooks/useIdleLogout';
import WhatsAppAdminButton from '../components/WhatsAppAdminButton';
import { getCarUnlockFee, getHouseUnlockFee } from '../utils/unlockFees';

const unlockFees: Record<string, number> = {
  'Bedsitter': 150,
  '1 Bedroom': 200,
  '2 Bedroom': 300,
  '3 Bedroom': 400,
  '4 Bedroom': 500,
};

// Share of each unlock fee that goes to the lister (rest goes to the platform).
// Applied to the NET amount after IntaSend's ~3% transaction fee is deducted —
// both sides absorb that fee proportionally, not just the platform.
const LISTER_SHARE_PERCENT = 60;
const PAYMENT_PROCESSOR_FEE_PERCENT = 3;

// Resize + re-compress a photo in the browser before it's uploaded, so a
// 6-15MB phone photo becomes a few hundred KB with no visible quality loss
// at the size these photos are actually viewed on. Falls back to the
// original file if anything goes wrong or if compression didn't help.
const MAX_PHOTO_DIMENSION = 1920;
const PHOTO_COMPRESSION_QUALITY = 0.82;

const compressImage = (file: File): Promise<File> => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) { resolve(file); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_PHOTO_DIMENSION || height > MAX_PHOTO_DIMENSION) {
          if (width > height) {
            height = Math.round((height * MAX_PHOTO_DIMENSION) / width);
            width = MAX_PHOTO_DIMENSION;
          } else {
            width = Math.round((width * MAX_PHOTO_DIMENSION) / height);
            height = MAX_PHOTO_DIMENSION;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(file); return; }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          const compressedFile = new File(
            [blob],
            file.name.replace(/\.\w+$/, '.jpg'),
            { type: 'image/jpeg', lastModified: Date.now() }
          );
          resolve(compressedFile.size < file.size ? compressedFile : file);
        }, 'image/jpeg', PHOTO_COMPRESSION_QUALITY);
      };
      img.onerror = () => resolve(file);
      img.src = e.target?.result as string;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
};

// Best-effort storage cleanup — turns a public Supabase Storage URL back into
// its bucket path and deletes the file. Never blocks the calling action if it fails.
const deleteStorageFile = async (bucket: string, publicUrl: string) => {
  try {
    const marker = `/${bucket}/`;
    const idx = publicUrl.indexOf(marker);
    if (idx === -1) return;
    const path = publicUrl.substring(idx + marker.length);
    if (!path) return;
    await supabase.storage.from(bucket).remove([path]);
  } catch {
    // Storage cleanup is best-effort; the listing/DB row is already gone by this point.
  }
};

const inputStyle = { width: '100%', background: 'var(--dark-3)', border: '1px solid rgba(201,168,76,0.2)', color: 'var(--text)', padding: '12px 16px', borderRadius: '4px', fontSize: '14px', outline: 'none' };
const labelStyle = { color: 'var(--text-muted)', fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase' as const, display: 'block', marginBottom: '8px' };

type ListingRow = {
  id: string;
  title: string;
  status: string;
  table: 'rentals' | 'land' | 'houses_for_sale' | 'cars';
  clicks?: number;
};

const isBlockedError = (message?: string) => !!message && message.toLowerCase().includes('blocked');

const ListerDashboardPage = () => {
  const [checkingSession, setCheckingSession] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [isBanned, setIsBanned] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const [calcType, setCalcType] = useState('Bedsitter');
  const [calcCount, setCalcCount] = useState('1');
  const [view, setView] = useState<'listings' | 'add'>('listings');
  const [activeTab, setActiveTab] = useState<'rental' | 'land' | 'house' | 'car'>('rental');
  const [myListings, setMyListings] = useState<ListingRow[]>([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const [rentalForm, setRentalForm] = useState({ title: '', building_name: '', type: 'Bedsitter', bedrooms: 0, price_per_month: '', description: '', whatsapp: '', google_maps_link: '' });
  const [landForm, setLandForm] = useState({
    title: '', county: '', location: '', size: '', price: '', type: 'Residential', description: '',
    whatsapp: '', second_whatsapp: '', google_maps_link: '', agent_name: '',
    water_on_site: '', water_in_area: '', electricity_on_site: '', electricity_in_area: '',
    title_deed: '', document_type: '', access_road_type: '', distance_to_tarmac: '',
    has_disputes: '', beacons_confirmed: '', nearby_amenities: '', negotiable: '', payment_plan_available: '',
  });
  const [houseForm, setHouseForm] = useState({ title: '', building_name: '', location: '', price: '', bedrooms: '', bathrooms: '', type: 'Apartment', description: '', whatsapp: '', google_maps_link: '' });
  const [carForm, setCarForm] = useState({ title: '', make: '', model: '', year: '', mileage: '', price: '', type: 'Sedan', transmission: 'Automatic', location: '', description: '', whatsapp: '' });

  const [listerForm, setListerForm] = useState({ role: 'Caretaker', whatsapp: '' });
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [agreementFile, setAgreementFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [editingListing, setEditingListing] = useState<{ id: string; table: ListingRow['table']; title: string } | null>(null);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [existingAgreementUrl, setExistingAgreementUrl] = useState<string | null>(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [needsProfile, setNeedsProfile] = useState(false);
  const [profileWhatsapp, setProfileWhatsapp] = useState('');
  const [profileRole, setProfileRole] = useState('Caretaker');
  const [profileError, setProfileError] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  const navigate = useNavigate();

  useIdleLogout(!!userId, '/lister-login');

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session) {
        navigate('/lister-login');
        return;
      }
      setUserId(session.user.id);
      setCheckingSession(false);

      const { data: profile } = await supabase.from('listers').select('banned, balance').eq('id', session.user.id).single();

      if (profile) {
        if (profile.banned) setIsBanned(true);
        if (typeof profile.balance === 'number') setBalance(profile.balance);
        return;
      }

      // No profile row yet — this is a first-time magic-link signup.
      // Try to auto-create it from the details they entered on the signup page.
      const pendingRaw = localStorage.getItem('pending-lister-signup');
      if (pendingRaw) {
        try {
          const pending = JSON.parse(pendingRaw);
          if (pending.email?.toLowerCase() === session.user.email?.toLowerCase()) {
            const { error: insertError } = await supabase.from('listers').insert({
              id: session.user.id,
              email: session.user.email,
              whatsapp: pending.whatsapp,
              role: pending.role,
              banned: false,
            });
            localStorage.removeItem('pending-lister-signup');
            if (!insertError) {
              setBalance(0);
              return;
            }
            if (isBlockedError(insertError.message)) {
              setIsBlocked(true);
              return;
            }
          }
        } catch {
          // fall through to manual completion below
        }
      }

      // No matching pending data (e.g. link opened on a different device) —
      // ask them to fill in the missing details right here instead.
      setNeedsProfile(true);
    });
  }, [navigate]);

  const handleCompleteProfile = async () => {
    setProfileError('');
    if (!profileWhatsapp.trim()) {
      setProfileError('Please enter a WhatsApp contact number.');
      return;
    }
    if (!userId) return;
    setProfileSaving(true);
    let normalizedWhatsapp = profileWhatsapp.replace(/\D/g, '');
    if (normalizedWhatsapp.startsWith('0')) normalizedWhatsapp = '254' + normalizedWhatsapp.slice(1);
    else if (!normalizedWhatsapp.startsWith('254')) normalizedWhatsapp = '254' + normalizedWhatsapp;

    const { data: { user } } = await supabase.auth.getUser();
    const { error: insertError } = await supabase.from('listers').insert({
      id: userId,
      email: user?.email,
      whatsapp: normalizedWhatsapp,
      role: profileRole,
      banned: false,
    });
    setProfileSaving(false);
    if (insertError) {
      if (isBlockedError(insertError.message)) {
        setNeedsProfile(false);
        setIsBlocked(true);
      } else if (insertError.code === '23505') {
        setProfileError('This WhatsApp number is already registered to another lister account.');
      } else if (insertError.message.includes('cap')) {
        setProfileError('We\u2019ve reached our cap of 20 listers for now — please contact admin.');
      } else {
        setProfileError(insertError.message);
      }
      return;
    }
    setBalance(0);
    setNeedsProfile(false);
  };

  useEffect(() => {
    if (userId) fetchMyListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchMyListings = async () => {
    if (!userId) return;
    setLoadingListings(true);
    const tables: ListingRow['table'][] = ['rentals', 'land', 'houses_for_sale', 'cars'];
    let all: ListingRow[] = [];
    for (const table of tables) {
      const { data, error: fetchError } = await supabase
        .from(table)
        .select('id, title, status')
        .eq('lister_id', userId);
      if (!fetchError && data) {
        all = all.concat(data.map((d: any) => ({ ...d, table })));
      }
    }

    const { data: clickData } = await supabase
      .from('link_clicks')
      .select('listing_id')
      .eq('lister_id', userId);

    const clickCounts: Record<string, number> = {};
    if (clickData) {
      clickData.forEach((c: any) => {
        clickCounts[c.listing_id] = (clickCounts[c.listing_id] || 0) + 1;
      });
    }

    all = all.map(item => ({ ...item, clicks: clickCounts[item.id] || 0 }));

    setMyListings(all);
    setLoadingListings(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/lister-login');
  };

  const handleDelete = async (row: ListingRow) => {
    if (!window.confirm(`Delete "${row.title}"? This also removes its photos from storage. This cannot be undone.`)) return;

    // Grab the photo/agreement URLs before the row is gone, so we can clean up storage too.
    const { data: fullRow } = await supabase.from(row.table).select('images, agreement_url').eq('id', row.id).single();

    const { error: deleteError } = await supabase.from(row.table).delete().eq('id', row.id);
    if (deleteError) {
      alert('Error deleting: ' + deleteError.message);
      return;
    }

    if (fullRow?.images && Array.isArray(fullRow.images)) {
      await Promise.all(fullRow.images.map((url: string) => deleteStorageFile('rental-images', url)));
    }
    if (fullRow?.agreement_url) {
      await deleteStorageFile('lister-agreements', fullRow.agreement_url);
    }

    fetchMyListings();
  };

  const tableToTab: Record<ListingRow['table'], 'rental' | 'land' | 'house' | 'car'> = {
    rentals: 'rental', land: 'land', houses_for_sale: 'house', cars: 'car',
  };

  const handleEdit = async (row: ListingRow) => {
    setLoadingEdit(true);
    setError(''); setSuccess('');
    const { data, error: fetchError } = await supabase.from(row.table).select('*').eq('id', row.id).single();
    setLoadingEdit(false);
    if (fetchError || !data) {
      alert('Could not load this listing: ' + (fetchError?.message || 'not found'));
      return;
    }

    const fromBool = (v: any) => (v === true ? 'Yes' : v === false ? 'No' : '');

    setImages([]); setPreviews([]); setAgreementFile(null);
    setExistingImages(data.images || []);
    setExistingAgreementUrl(data.agreement_url || null);
    setVideoUrl(data.video_url || '');
    setListerForm({ role: data.lister_role || 'Caretaker', whatsapp: data.lister_whatsapp || '' });

    if (row.table === 'rentals') {
      setRentalForm({
        title: data.title || '', building_name: data.building_name || '', type: data.type || 'Bedsitter',
        bedrooms: data.bedrooms || 0, price_per_month: String(data.price_per_month ?? ''), description: data.description || '',
        whatsapp: data.whatsapp || '', google_maps_link: data.google_maps_link || '',
      });
    } else if (row.table === 'land') {
      setLandForm({
        title: data.title || '', county: data.county || '', location: data.location || '', size: data.size || '',
        price: String(data.price ?? ''), type: data.type || 'Residential', description: data.description || '',
        whatsapp: data.whatsapp || '', second_whatsapp: data.second_whatsapp || '', google_maps_link: data.google_maps_link || '',
        agent_name: data.agent_name || '',
        water_on_site: fromBool(data.water_on_site), water_in_area: fromBool(data.water_in_area),
        electricity_on_site: fromBool(data.electricity_on_site), electricity_in_area: fromBool(data.electricity_in_area),
        title_deed: fromBool(data.title_deed), document_type: data.document_type || '',
        access_road_type: data.access_road_type || '', distance_to_tarmac: data.distance_to_tarmac || '',
        has_disputes: fromBool(data.has_disputes), beacons_confirmed: fromBool(data.beacons_confirmed),
        nearby_amenities: data.nearby_amenities || '', negotiable: fromBool(data.negotiable),
        payment_plan_available: fromBool(data.payment_plan_available),
      });
    } else if (row.table === 'houses_for_sale') {
      setHouseForm({
        title: data.title || '', building_name: data.building_name || '', location: data.location || '',
        price: String(data.price ?? ''), bedrooms: String(data.bedrooms ?? ''), bathrooms: String(data.bathrooms ?? ''),
        type: data.type || 'Apartment', description: data.description || '', whatsapp: data.whatsapp || '',
        google_maps_link: data.google_maps_link || '',
      });
    } else if (row.table === 'cars') {
      setCarForm({
        title: data.title || '', make: data.make || '', model: data.model || '', year: String(data.year ?? ''),
        mileage: data.mileage || '', price: String(data.price ?? ''), type: data.type || 'Sedan',
        transmission: data.transmission || 'Automatic', location: data.location || '', description: data.description || '',
        whatsapp: data.whatsapp || '',
      });
    }

    setActiveTab(tableToTab[row.table]);
    setEditingListing({ id: row.id, table: row.table, title: data.title || row.title });
    setView('add');
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const max = activeTab === 'land' ? 10 : 7;
    if (files.length > max) { setError(`Maximum ${max} images!`); return; }
    setError('');
    setCompressing(true);
    try {
      const compressed = await Promise.all(files.map(f => compressImage(f)));
      setImages(compressed);
      setPreviews(compressed.map(f => URL.createObjectURL(f)));
    } finally {
      setCompressing(false);
    }
  };

  const uploadImages = async () => {
    const urls: string[] = [];
    for (let i = 0; i < images.length; i++) {
      const file = images[i];
      const fileName = `${Date.now()}-${i}-${file.name}`;
      const { error: uploadError } = await supabase.storage.from('rental-images').upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from('rental-images').getPublicUrl(fileName);
      urls.push(data.publicUrl);
    }
    return urls;
  };

  const uploadAgreement = async () => {
    if (!agreementFile) return null;
    const fileName = `${Date.now()}-${agreementFile.name}`;
    const { error: uploadError } = await supabase.storage.from('lister-agreements').upload(fileName, agreementFile);
    if (uploadError) throw uploadError;
    // Private bucket — store just the file path, not a public URL.
    return fileName;
  };

  const resetForm = () => {
    setImages([]); setPreviews([]); setSuccess(''); setError('');
    setAgreementFile(null); setVideoUrl('');
    setEditingListing(null); setExistingImages([]); setExistingAgreementUrl(null);
    setListerForm({ role: 'Caretaker', whatsapp: '' });
    setRentalForm({ title: '', building_name: '', type: 'Bedsitter', bedrooms: 0, price_per_month: '', description: '', whatsapp: '', google_maps_link: '' });
    setLandForm({
      title: '', county: '', location: '', size: '', price: '', type: 'Residential', description: '',
      whatsapp: '', second_whatsapp: '', google_maps_link: '', agent_name: '',
      water_on_site: '', water_in_area: '', electricity_on_site: '', electricity_in_area: '',
      title_deed: '', document_type: '', access_road_type: '', distance_to_tarmac: '',
      has_disputes: '', beacons_confirmed: '', nearby_amenities: '', negotiable: '', payment_plan_available: '',
    });
    setHouseForm({ title: '', building_name: '', location: '', price: '', bedrooms: '', bathrooms: '', type: 'Apartment', description: '', whatsapp: '', google_maps_link: '' });
    setCarForm({ title: '', make: '', model: '', year: '', mileage: '', price: '', type: 'Sedan', transmission: 'Automatic', location: '', description: '', whatsapp: '' });
  };

  const handleSubmit = async () => {
    if (!userId) return;
    setLoading(true); setError(''); setSuccess('');
    const isEditing = !!editingListing;
    try {
      // Only re-upload photos if the lister picked new ones — otherwise keep the current photos as-is.
      const imageUrls = images.length > 0 ? await uploadImages() : existingImages;
      const uploadedAgreementUrl = await uploadAgreement();
      const agreementUrl = uploadedAgreementUrl !== null ? uploadedAgreementUrl : existingAgreementUrl;

      // Base fields shared across categories. On edit, we deliberately don't touch
      // lister_id or status — editing shouldn't change ownership or hide/re-show a listing.
      const listerData: any = { lister_role: listerForm.role, lister_whatsapp: listerForm.whatsapp };
      if (activeTab !== 'land') listerData.agreement_url = agreementUrl;
      if (!isEditing) { listerData.lister_id = userId; listerData.status = 'live'; }

      if (activeTab === 'rental') {
        const payload = { ...rentalForm, price_per_month: parseInt(rentalForm.price_per_month), unlock_fee: unlockFees[rentalForm.type], images: imageUrls, video_url: videoUrl || null, ...listerData };
        const { error: e } = isEditing
          ? await supabase.from('rentals').update(payload).eq('id', editingListing!.id)
          : await supabase.from('rentals').insert(payload);
        if (e) throw e;
      } else if (activeTab === 'land') {
        const toBool = (v: string) => v === 'Yes' ? true : v === 'No' ? false : null;
        const payload = {
          title: landForm.title,
          county: landForm.county,
          location: landForm.location,
          size: landForm.size,
          price: parseInt(landForm.price),
          type: landForm.type,
          description: landForm.description,
          whatsapp: landForm.whatsapp,
          second_whatsapp: landForm.second_whatsapp || null,
          google_maps_link: landForm.google_maps_link,
          agent_name: landForm.agent_name || null,
          water_on_site: toBool(landForm.water_on_site),
          water_in_area: toBool(landForm.water_in_area),
          electricity_on_site: toBool(landForm.electricity_on_site),
          electricity_in_area: toBool(landForm.electricity_in_area),
          title_deed: toBool(landForm.title_deed),
          document_type: landForm.document_type || null,
          access_road_type: landForm.access_road_type || null,
          distance_to_tarmac: landForm.distance_to_tarmac || null,
          has_disputes: toBool(landForm.has_disputes),
          beacons_confirmed: toBool(landForm.beacons_confirmed),
          nearby_amenities: landForm.nearby_amenities || null,
          negotiable: toBool(landForm.negotiable),
          payment_plan_available: toBool(landForm.payment_plan_available),
          unlock_fee: 150,
          images: imageUrls,
          video_url: videoUrl || null,
          ...listerData,
        };
        const { error: e } = isEditing
          ? await supabase.from('land').update(payload).eq('id', editingListing!.id)
          : await supabase.from('land').insert(payload);
        if (e) throw e;
      } else if (activeTab === 'house') {
        const housePrice = parseInt(houseForm.price);
        const payload = { ...houseForm, price: housePrice, bedrooms: parseInt(houseForm.bedrooms), bathrooms: parseInt(houseForm.bathrooms), unlock_fee: getHouseUnlockFee(housePrice), images: imageUrls, video_url: videoUrl || null, ...listerData };
        const { error: e } = isEditing
          ? await supabase.from('houses_for_sale').update(payload).eq('id', editingListing!.id)
          : await supabase.from('houses_for_sale').insert(payload);
        if (e) throw e;
      } else if (activeTab === 'car') {
        const carPrice = parseInt(carForm.price);
        const payload: any = { ...carForm, year: parseInt(carForm.year), price: carPrice, unlock_fee: getCarUnlockFee(carPrice), images: imageUrls, lister_role: listerForm.role, lister_whatsapp: listerForm.whatsapp, video_url: videoUrl || null };
        if (!isEditing) { payload.lister_id = userId; payload.status = 'live'; }
        const { error: e } = isEditing
          ? await supabase.from('cars').update(payload).eq('id', editingListing!.id)
          : await supabase.from('cars').insert(payload);
        if (e) throw e;
      }
      setSuccess(isEditing ? '✅ Listing updated!' : '✅ Listing submitted! It will appear publicly once approved by admin.');
      resetForm();
      fetchMyListings();
      setView('listings');
    } catch (err: any) {
      setError(err.message || 'Something went wrong!');
    }
    setLoading(false);
  };

  if (checkingSession) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--dark)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
      </div>
    );
  }

  if (isBlocked) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: 'var(--dark-3)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '8px', padding: '48px', width: '100%', maxWidth: '420px', textAlign: 'center' }}>
          <h1 className="serif" style={{ color: '#f87171', fontSize: '26px', fontWeight: 300, marginBottom: '16px' }}>Account Not Available</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '24px', lineHeight: 1.6 }}>
            This email or WhatsApp number isn't able to register as a lister. If you believe this is a mistake, contact the admin below.
          </p>
          <WhatsAppAdminButton />
        </div>
      </div>
    );
  }

  if (needsProfile) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: 'var(--dark-3)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '8px', padding: '40px', width: '100%', maxWidth: '400px' }}>
          <h1 className="serif" style={{ color: 'var(--gold)', fontSize: '24px', fontWeight: 300, marginBottom: '8px' }}>One More Step</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '24px' }}>
            We just need a couple of details to finish setting up your lister account.
          </p>
          <label style={labelStyle}>WhatsApp Contact</label>
          <input value={profileWhatsapp} onChange={e => setProfileWhatsapp(e.target.value)} placeholder="e.g. 0712345678" style={{ ...inputStyle, marginBottom: '16px' }} />
          <label style={labelStyle}>Role</label>
          <select value={profileRole} onChange={e => setProfileRole(e.target.value)} style={{ ...inputStyle, marginBottom: '20px' }}>
            <option>Caretaker</option>
            <option>Agent</option>
            <option>Owner</option>
          </select>
          {profileError && <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '16px' }}>{profileError}</p>}
          <button onClick={handleCompleteProfile} className="btn-primary" style={{ width: '100%' }} disabled={profileSaving}>
            {profileSaving ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  if (isBanned) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: 'var(--dark-3)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '8px', padding: '48px', width: '100%', maxWidth: '420px', textAlign: 'center' }}>
          <h1 className="serif" style={{ color: '#f87171', fontSize: '26px', fontWeight: 300, marginBottom: '16px' }}>Account Suspended</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '24px', lineHeight: 1.6 }}>
            Your lister account has been temporarily suspended. Contact the admin below if you believe this is a mistake.
          </p>
          <WhatsAppAdminButton />
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--dark)', paddingTop: '100px', paddingBottom: '80px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <p style={{ color: 'var(--gold)', fontSize: '11px', letterSpacing: '0.3em', textTransform: 'uppercase' }}>Lister Dashboard</p>
          <button onClick={handleSignOut}
            style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
            Sign Out
          </button>
        </div>

        <h1 className="serif" style={{ fontSize: '42px', fontWeight: 300, color: 'var(--text)', marginBottom: '16px' }}>My <span style={{ color: 'var(--gold)' }}>Listings</span></h1>

        {balance !== null && (
          <div style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '8px', padding: '16px 20px', marginBottom: '16px' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Earnings (paid out monthly)</p>
            <p style={{ color: 'var(--gold)', fontSize: '24px', fontWeight: 600 }}>KSh {balance.toLocaleString()}</p>
          </div>
        )}

        <div style={{ background: 'var(--dark-3)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '8px', padding: '16px 20px', marginBottom: '16px' }}>
          <p style={{ color: 'var(--gold)', fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px', fontWeight: 600 }}>Earnings Calculator</p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '12px' }}>
            <div>
              <label style={{ color: 'var(--text-muted)', fontSize: '11px', display: 'block', marginBottom: '6px' }}>House Type</label>
              <select value={calcType} onChange={e => setCalcType(e.target.value)} style={{ ...inputStyle, width: '160px' }}>
                {Object.keys(unlockFees).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ color: 'var(--text-muted)', fontSize: '11px', display: 'block', marginBottom: '6px' }}># of Unlocks</label>
              <input type="number" min="0" value={calcCount} onChange={e => setCalcCount(e.target.value)} style={{ ...inputStyle, width: '100px' }} />
            </div>
          </div>
          {(() => {
            const count = parseInt(calcCount) || 0;
            const fee = unlockFees[calcType] || 0;
            const total = count * fee;
            const processorFee = Math.round(total * (PAYMENT_PROCESSOR_FEE_PERCENT / 100));
            const net = total - processorFee;
            const yourShare = Math.round(net * (LISTER_SHARE_PERCENT / 100));
            return (
              <p style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.7 }}>
                {count} × KSh {fee} = KSh {total.toLocaleString()} collected, minus ~KSh {processorFee.toLocaleString()} payment processing fee ={' '}
                KSh {net.toLocaleString()} net — at your {LISTER_SHARE_PERCENT}% share, that's{' '}
                <span style={{ color: 'var(--gold)', fontWeight: 600 }}>KSh {yourShare.toLocaleString()}</span> for you.
              </p>
            );
          })()}
          <p style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '8px', fontStyle: 'italic' }}>
            Estimate only — actual earnings are added to your balance above once payments are confirmed.
          </p>
        </div>

        <p style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6, marginBottom: '32px' }}>
          💡 Share your listing links on WhatsApp status, groups, and with people you know — more people seeing it means more chances someone unlocks and pays, which adds up by month end.
        </p>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '40px' }}>
          <button onClick={() => setView('listings')}
            style={{ padding: '10px 20px', borderRadius: '50px', border: '1px solid rgba(201,168,76,0.3)', background: view === 'listings' ? 'rgba(201,168,76,0.15)' : 'transparent', color: view === 'listings' ? 'var(--gold)' : 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
            📋 My Listings
          </button>
          <button onClick={() => { setView('add'); resetForm(); }}
            style={{ padding: '10px 20px', borderRadius: '50px', border: '1px solid rgba(201,168,76,0.3)', background: view === 'add' ? 'rgba(201,168,76,0.15)' : 'transparent', color: view === 'add' ? 'var(--gold)' : 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
            ➕ Add New Listing
          </button>
        </div>

        {view === 'listings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {loadingListings && <p style={{ color: 'var(--text-muted)' }}>Loading...</p>}
            {!loadingListings && myListings.length === 0 && (
              <p style={{ color: 'var(--text-muted)' }}>You haven't submitted any listings yet. Click "Add New Listing" to get started.</p>
            )}
            {myListings.map(row => (
              <div key={`${row.table}-${row.id}`} style={{ background: 'rgba(201,168,76,0.03)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: '8px', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <p style={{ color: 'var(--text)', fontSize: '15px', fontWeight: 500 }}>{row.title}</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>
                    {row.table.replace(/_/g, ' ')} · 👁 {row.clicks || 0} view{row.clicks === 1 ? '' : 's'} from shared links
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    fontSize: '11px', padding: '4px 10px', borderRadius: '50px',
                    background: row.status === 'banned' ? 'rgba(248,113,113,0.15)' : 'rgba(74,222,128,0.15)',
                    color: row.status === 'banned' ? '#f87171' : '#4ade80',
                  }}>
                    {row.status === 'banned' ? 'Hidden by Admin' : 'Live'}
                  </span>
                  <button onClick={() => handleEdit(row)} disabled={loadingEdit}
                    style={{ background: 'transparent', border: '1px solid rgba(201,168,76,0.3)', color: 'var(--gold)', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', cursor: loadingEdit ? 'default' : 'pointer' }}>
                    {loadingEdit ? 'Loading...' : 'Edit'}
                  </button>
                  <button onClick={() => handleDelete(row)}
                    style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {view === 'add' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {editingListing && (
              <div style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '8px', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                <p style={{ color: 'var(--gold)', fontSize: '13px' }}>✏️ Editing "{editingListing.title}"</p>
                <button onClick={() => { resetForm(); setView('listings'); }}
                  style={{ background: 'transparent', border: '1px solid rgba(201,168,76,0.3)', color: 'var(--text-muted)', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                  Cancel Edit
                </button>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                { key: 'rental', label: '🔑 Rental' },
                { key: 'land', label: '🌍 Land' },
                { key: 'house', label: '🏠 House for Sale' },
                { key: 'car', label: '🚗 Car' },
              ].map(tab => {
                const locked = !!editingListing && tab.key !== activeTab;
                return (
                  <button key={tab.key} disabled={locked} onClick={() => { setActiveTab(tab.key as any); resetForm(); }}
                    style={{ padding: '10px 20px', borderRadius: '50px', border: '1px solid rgba(201,168,76,0.3)', background: activeTab === tab.key ? 'rgba(201,168,76,0.15)' : 'transparent', color: activeTab === tab.key ? 'var(--gold)' : 'var(--text-muted)', cursor: locked ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 500, opacity: locked ? 0.35 : 1 }}>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {activeTab === 'rental' && <>
              <div><label style={labelStyle}>Listing Title</label><input value={rentalForm.title} onChange={e => setRentalForm({ ...rentalForm, title: e.target.value })} placeholder="e.g. Modern Bedsitter - Kilimani" style={inputStyle} /></div>
              <div><label style={labelStyle}>Building Name</label><input value={rentalForm.building_name} onChange={e => setRentalForm({ ...rentalForm, building_name: e.target.value })} placeholder="e.g. Sunrise Apartments" style={inputStyle} /></div>
              <div><label style={labelStyle}>House Type</label>
                <select value={rentalForm.type} onChange={e => setRentalForm({ ...rentalForm, type: e.target.value })} style={inputStyle}>
                  {Object.keys(unlockFees).map(t => <option key={t} value={t}>{t} — Unlock: KSh {unlockFees[t]}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Monthly Rent (KSh)</label><input type="number" value={rentalForm.price_per_month} onChange={e => setRentalForm({ ...rentalForm, price_per_month: e.target.value })} placeholder="e.g. 15000" style={inputStyle} /></div>
              <div><label style={labelStyle}>Description</label><textarea value={rentalForm.description} onChange={e => setRentalForm({ ...rentalForm, description: e.target.value })} placeholder="Describe the property..." rows={4} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              <div><label style={labelStyle}>Caretaker WhatsApp</label><input value={rentalForm.whatsapp} onChange={e => setRentalForm({ ...rentalForm, whatsapp: e.target.value })} placeholder="e.g. 0712345678" style={inputStyle} /></div>
              <div><label style={labelStyle}>Google Maps Link</label><input value={rentalForm.google_maps_link} onChange={e => setRentalForm({ ...rentalForm, google_maps_link: e.target.value })} placeholder="Paste Google Maps link" style={inputStyle} /></div>
            </>}

            {activeTab === 'land' && <>
              <div style={{ background: 'rgba(248,113,113,0.05)', border: '1px solid rgba(248,113,113,0.15)', borderRadius: '6px', padding: '14px 16px' }}>
                <p style={{ color: '#f87171', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Land Listing Rules</p>
                <p style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6 }}>
                  1. False or misleading information → indefinite ban.<br />
                  2. Forfeiture of all accumulated funds.<br />
                  3. A public ban notice will name the listing as fraudulent.<br />
                  4. Listed price must be genuine — no bait pricing.
                </p>
              </div>
              <div><label style={labelStyle}>Listing Title <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(do not mention your company name)</span></label><input value={landForm.title} onChange={e => setLandForm({ ...landForm, title: e.target.value })} placeholder="e.g. Prime Plot - Kiambu" style={inputStyle} /></div>
              <div><label style={labelStyle}>County</label><input value={landForm.county} onChange={e => setLandForm({ ...landForm, county: e.target.value })} placeholder="e.g. Kiambu" style={inputStyle} /></div>
              <div><label style={labelStyle}>Location</label><input value={landForm.location} onChange={e => setLandForm({ ...landForm, location: e.target.value })} placeholder="e.g. Ruiru, Juja Farm, Kilimambogo" style={inputStyle} /></div>
              <div><label style={labelStyle}>Size</label><input value={landForm.size} onChange={e => setLandForm({ ...landForm, size: e.target.value })} placeholder="e.g. 50x100 ft or 2 Acres" style={inputStyle} /></div>
              <div><label style={labelStyle}>Price (KSh)</label><input type="number" value={landForm.price} onChange={e => setLandForm({ ...landForm, price: e.target.value })} placeholder="e.g. 2500000" style={inputStyle} /></div>
              <div><label style={labelStyle}>Type</label>
                <select value={landForm.type} onChange={e => setLandForm({ ...landForm, type: e.target.value })} style={inputStyle}>
                  {['Residential', 'Agricultural', 'Commercial', 'Beach'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Description</label><textarea value={landForm.description} onChange={e => setLandForm({ ...landForm, description: e.target.value })} placeholder="Describe the land..." rows={4} style={{ ...inputStyle, resize: 'vertical' }} /></div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div><label style={labelStyle}>Water on-site?</label>
                  <select value={landForm.water_on_site} onChange={e => setLandForm({ ...landForm, water_on_site: e.target.value })} style={inputStyle}>
                    <option value="">Not specified</option><option>Yes</option><option>No</option>
                  </select>
                </div>
                <div><label style={labelStyle}>Water in the area?</label>
                  <select value={landForm.water_in_area} onChange={e => setLandForm({ ...landForm, water_in_area: e.target.value })} style={inputStyle}>
                    <option value="">Not specified</option><option>Yes</option><option>No</option>
                  </select>
                </div>
                <div><label style={labelStyle}>Electricity on-site?</label>
                  <select value={landForm.electricity_on_site} onChange={e => setLandForm({ ...landForm, electricity_on_site: e.target.value })} style={inputStyle}>
                    <option value="">Not specified</option><option>Yes</option><option>No</option>
                  </select>
                </div>
                <div><label style={labelStyle}>Electricity in the area?</label>
                  <select value={landForm.electricity_in_area} onChange={e => setLandForm({ ...landForm, electricity_in_area: e.target.value })} style={inputStyle}>
                    <option value="">Not specified</option><option>Yes</option><option>No</option>
                  </select>
                </div>
                <div><label style={labelStyle}>Title deed?</label>
                  <select value={landForm.title_deed} onChange={e => setLandForm({ ...landForm, title_deed: e.target.value })} style={inputStyle}>
                    <option value="">Not specified</option><option>Yes</option><option>No</option>
                  </select>
                </div>
                <div><label style={labelStyle}>Beacons confirmed?</label>
                  <select value={landForm.beacons_confirmed} onChange={e => setLandForm({ ...landForm, beacons_confirmed: e.target.value })} style={inputStyle}>
                    <option value="">Not specified</option><option>Yes</option><option>No</option>
                  </select>
                </div>
                <div><label style={labelStyle}>Any disputes/caveats?</label>
                  <select value={landForm.has_disputes} onChange={e => setLandForm({ ...landForm, has_disputes: e.target.value })} style={inputStyle}>
                    <option value="">Not specified</option><option>Yes</option><option>No</option>
                  </select>
                </div>
                <div><label style={labelStyle}>Negotiable?</label>
                  <select value={landForm.negotiable} onChange={e => setLandForm({ ...landForm, negotiable: e.target.value })} style={inputStyle}>
                    <option value="">Not specified</option><option>Yes</option><option>No</option>
                  </select>
                </div>
                <div><label style={labelStyle}>Payment plan available?</label>
                  <select value={landForm.payment_plan_available} onChange={e => setLandForm({ ...landForm, payment_plan_available: e.target.value })} style={inputStyle}>
                    <option value="">Not specified</option><option>Yes</option><option>No</option>
                  </select>
                </div>
              </div>

              <div><label style={labelStyle}>Document type <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(if not full title deed)</span></label><input value={landForm.document_type} onChange={e => setLandForm({ ...landForm, document_type: e.target.value })} placeholder="e.g. Allotment letter, Sale agreement" style={inputStyle} /></div>
              <div><label style={labelStyle}>Access road type</label><input value={landForm.access_road_type} onChange={e => setLandForm({ ...landForm, access_road_type: e.target.value })} placeholder="e.g. Tarmac, Murram, Footpath" style={inputStyle} /></div>
              <div><label style={labelStyle}>Distance to tarmac</label><input value={landForm.distance_to_tarmac} onChange={e => setLandForm({ ...landForm, distance_to_tarmac: e.target.value })} placeholder="e.g. 500m" style={inputStyle} /></div>
              <div><label style={labelStyle}>Nearby amenities</label><input value={landForm.nearby_amenities} onChange={e => setLandForm({ ...landForm, nearby_amenities: e.target.value })} placeholder="e.g. School, hospital, market, matatu stage" style={inputStyle} /></div>

              <div><label style={labelStyle}>Agent/Manager Name</label><input value={landForm.agent_name} onChange={e => setLandForm({ ...landForm, agent_name: e.target.value })} placeholder="Shown to buyers after they unlock" style={inputStyle} /></div>
              <div><label style={labelStyle}>Primary WhatsApp Contact</label><input value={landForm.whatsapp} onChange={e => setLandForm({ ...landForm, whatsapp: e.target.value })} placeholder="e.g. 0712345678" style={inputStyle} /></div>
              <div><label style={labelStyle}>Second WhatsApp Contact (optional)</label><input value={landForm.second_whatsapp} onChange={e => setLandForm({ ...landForm, second_whatsapp: e.target.value })} placeholder="e.g. 0712345678" style={inputStyle} /></div>
              <div><label style={labelStyle}>Google Maps Link</label><input value={landForm.google_maps_link} onChange={e => setLandForm({ ...landForm, google_maps_link: e.target.value })} placeholder="Paste Google Maps link" style={inputStyle} /></div>
            </>}

            {activeTab === 'house' && <>
              <div><label style={labelStyle}>Listing Title</label><input value={houseForm.title} onChange={e => setHouseForm({ ...houseForm, title: e.target.value })} placeholder="e.g. Modern Apartment - Westlands" style={inputStyle} /></div>
              <div><label style={labelStyle}>Building Name</label><input value={houseForm.building_name} onChange={e => setHouseForm({ ...houseForm, building_name: e.target.value })} placeholder="e.g. Greenpark Estate" style={inputStyle} /></div>
              <div><label style={labelStyle}>Location</label><input value={houseForm.location} onChange={e => setHouseForm({ ...houseForm, location: e.target.value })} placeholder="e.g. Westlands, Nairobi" style={inputStyle} /></div>
              <div><label style={labelStyle}>Price (KSh)</label><input type="number" value={houseForm.price} onChange={e => setHouseForm({ ...houseForm, price: e.target.value })} placeholder="e.g. 8500000" style={inputStyle} />
                {houseForm.price && !isNaN(parseInt(houseForm.price)) && (
                  <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '6px' }}>Unlock fee for buyers: KSh {getHouseUnlockFee(parseInt(houseForm.price))}</p>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div><label style={labelStyle}>Bedrooms</label><input type="number" value={houseForm.bedrooms} onChange={e => setHouseForm({ ...houseForm, bedrooms: e.target.value })} placeholder="e.g. 3" style={inputStyle} /></div>
                <div><label style={labelStyle}>Bathrooms</label><input type="number" value={houseForm.bathrooms} onChange={e => setHouseForm({ ...houseForm, bathrooms: e.target.value })} placeholder="e.g. 2" style={inputStyle} /></div>
              </div>
              <div><label style={labelStyle}>Type</label>
                <select value={houseForm.type} onChange={e => setHouseForm({ ...houseForm, type: e.target.value })} style={inputStyle}>
                  {['Apartment', 'Bungalow', 'Townhouse', 'Mansion', 'Beach House'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Description</label><textarea value={houseForm.description} onChange={e => setHouseForm({ ...houseForm, description: e.target.value })} placeholder="Describe the property..." rows={4} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              <div><label style={labelStyle}>WhatsApp Contact</label><input value={houseForm.whatsapp} onChange={e => setHouseForm({ ...houseForm, whatsapp: e.target.value })} placeholder="e.g. 0712345678" style={inputStyle} /></div>
              <div><label style={labelStyle}>Google Maps Link</label><input value={houseForm.google_maps_link} onChange={e => setHouseForm({ ...houseForm, google_maps_link: e.target.value })} placeholder="Paste Google Maps link" style={inputStyle} /></div>
            </>}

            {activeTab === 'car' && <>
              <div><label style={labelStyle}>Listing Title</label><input value={carForm.title} onChange={e => setCarForm({ ...carForm, title: e.target.value })} placeholder="e.g. Toyota Land Cruiser V8" style={inputStyle} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div><label style={labelStyle}>Make</label><input value={carForm.make} onChange={e => setCarForm({ ...carForm, make: e.target.value })} placeholder="e.g. Toyota" style={inputStyle} /></div>
                <div><label style={labelStyle}>Model</label><input value={carForm.model} onChange={e => setCarForm({ ...carForm, model: e.target.value })} placeholder="e.g. Land Cruiser" style={inputStyle} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div><label style={labelStyle}>Year</label><input type="number" value={carForm.year} onChange={e => setCarForm({ ...carForm, year: e.target.value })} placeholder="e.g. 2022" style={inputStyle} /></div>
                <div><label style={labelStyle}>Mileage</label><input value={carForm.mileage} onChange={e => setCarForm({ ...carForm, mileage: e.target.value })} placeholder="e.g. 15,000 km" style={inputStyle} /></div>
              </div>
              <div><label style={labelStyle}>Price (KSh)</label><input type="number" value={carForm.price} onChange={e => setCarForm({ ...carForm, price: e.target.value })} placeholder="e.g. 3500000" style={inputStyle} />
                {carForm.price && !isNaN(parseInt(carForm.price)) && (
                  <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '6px' }}>Unlock fee for buyers: KSh {getCarUnlockFee(parseInt(carForm.price))}</p>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div><label style={labelStyle}>Type</label>
                  <select value={carForm.type} onChange={e => setCarForm({ ...carForm, type: e.target.value })} style={inputStyle}>
                    {['Sedan', 'SUV', 'Truck', 'Hatchback', 'Van'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Transmission</label>
                  <select value={carForm.transmission} onChange={e => setCarForm({ ...carForm, transmission: e.target.value })} style={inputStyle}>
                    {['Automatic', 'Manual', 'CVT'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div><label style={labelStyle}>Location</label><input value={carForm.location} onChange={e => setCarForm({ ...carForm, location: e.target.value })} placeholder="e.g. Nairobi" style={inputStyle} /></div>
              <div><label style={labelStyle}>Description</label><textarea value={carForm.description} onChange={e => setCarForm({ ...carForm, description: e.target.value })} placeholder="Describe the car..." rows={4} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              <div><label style={labelStyle}>WhatsApp Contact</label><input value={carForm.whatsapp} onChange={e => setCarForm({ ...carForm, whatsapp: e.target.value })} placeholder="e.g. 0712345678" style={inputStyle} /></div>
            </>}

            <div style={{ background: 'rgba(201,168,76,0.03)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: '8px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <p style={{ color: 'var(--gold)', fontSize: '11px', letterSpacing: '0.2em', textTransform: 'uppercase' }}>🔐 Your Verification Info</p>
              <div><label style={labelStyle}>Your Role</label>
                <select value={listerForm.role} onChange={e => setListerForm({ ...listerForm, role: e.target.value })} style={inputStyle}>
                  {(activeTab === 'land' ? ['Agent', 'Manager', 'Director', 'CEO'] : ['Caretaker', 'Owner', 'Building Manager', 'Agent']).map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Your WhatsApp Number</label>
                <input value={listerForm.whatsapp} onChange={e => setListerForm({ ...listerForm, whatsapp: e.target.value })} placeholder="e.g. 0712345678" style={inputStyle} />
              </div>
              {activeTab !== 'land' && (
                <div>
                  <label style={labelStyle}>Building Agreement (Optional but Recommended)</label>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setAgreementFile(e.target.files?.[0] || null)} style={{ color: 'var(--text)', fontSize: '13px' }} />
                </div>
              )}
            </div>

            <div>
              <label style={labelStyle}>Photos (Max {activeTab === 'land' ? 10 : 7} — First photo must be front/main photo)</label>
              {activeTab === 'rental' && (
                <div style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '6px', padding: '12px 14px', marginBottom: '10px' }}>
                  <p style={{ color: 'var(--gold)', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Required photo order for rentals:</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6 }}>
                    <strong style={{ color: 'var(--text)' }}>Photo 1:</strong> Front of the building, clearly visible<br />
                    <strong style={{ color: 'var(--text)' }}>Photo 2:</strong> The unit's door, with the door number clearly visible
                  </p>
                </div>
              )}
              {editingListing && previews.length === 0 && (
                <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginBottom: '10px' }}>
                  Leave this blank to keep the current photos below. Choosing new photos will replace all of them.
                </p>
              )}
              <input type="file" accept="image/*" multiple onChange={handleImageChange} style={{ color: 'var(--text)', fontSize: '13px' }} />
              {compressing && <p style={{ color: 'var(--gold)', fontSize: '12px', marginTop: '8px' }}>⏳ Compressing photos for a faster upload...</p>}
              <p style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '6px' }}>Photos are automatically resized and compressed for faster, more reliable uploads — no need to shrink them yourself first.</p>
              {previews.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px', marginTop: '16px' }}>
                  {previews.map((url, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={url} alt={`preview-${i}`} style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '4px', border: i === 0 ? '2px solid var(--gold)' : '1px solid rgba(201,168,76,0.2)' }} />
                      {i === 0 && <span style={{ position: 'absolute', bottom: '4px', left: '4px', background: 'var(--gold)', color: '#000', fontSize: '9px', padding: '2px 6px', borderRadius: '2px' }}>MAIN</span>}
                    </div>
                  ))}
                </div>
              )}
              {editingListing && previews.length === 0 && existingImages.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '11px', marginBottom: '8px' }}>Current photos:</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px' }}>
                    {existingImages.map((url, i) => (
                      <img key={i} src={url} alt={`current-${i}`} style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '4px', border: i === 0 ? '2px solid var(--gold)' : '1px solid rgba(201,168,76,0.2)' }} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label style={labelStyle}>Video Tour Link (optional, recommended)</label>
              <input value={videoUrl} onChange={e => setVideoUrl(e.target.value)}
                placeholder="Paste a YouTube or Google Drive link" style={inputStyle} />
              <p style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '6px' }}>
                A short walkthrough video builds trust — record on your phone, upload to YouTube (unlisted is fine) or Google Drive, then paste the link here.
              </p>
            </div>

            {error && <p style={{ color: '#f87171', fontSize: '13px' }}>{error}</p>}
            {success && <p style={{ color: '#4ade80', fontSize: '13px' }}>{success}</p>}

            <button onClick={handleSubmit} className="btn-primary" disabled={loading || compressing} style={{ padding: '16px', fontSize: '14px' }}>
              {compressing ? 'Compressing photos...' : loading ? (editingListing ? 'Saving...' : 'Uploading...') : (editingListing ? 'Save Changes' : 'Publish Listing')}
            </button>
          </div>
        )}

        <div style={{ marginTop: '48px', paddingTop: '24px', borderTop: '1px solid rgba(201,168,76,0.15)', maxWidth: '320px' }}>
          <WhatsAppAdminButton />
        </div>
      </div>
    </div>
  );
};

export default ListerDashboardPage;
